import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react"
import { SuccessContext } from "@app/components/layout/success-snackbar"
import { MyUserUpdateDto } from "@app/core/dto/user/my-user-update-dto"
import { User } from "@app/core/dto/user/user"
import { getErrorCode, getErrorMessage } from "@app/core/error/error-handler"
import { useUser } from "@app/core/hooks/use-user"
import { getUserFromStorage } from "@app/core/services/authentication-service"
import { Children } from "@app/core/type/Children"
import { TokenContext } from "./TokenContext"

export const UserContext = React.createContext<UserStore>({} as UserStore)

export function UserContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const initialUser: User | undefined = useMemo(() => getUserFromStorage(), [])

  const { getCurrentUser, updateMyUser } = useUser()
  const openSuccessSnackbar: (message: string) => void = React.useContext(SuccessContext)
  const { cognitoUserIdRef, unsetCookieAndUserId, isAuthenticated } = React.useContext(TokenContext)

  const [user, setUser] = useState<User | undefined>(initialUser)
  const [userLoading, setUserLoading] = useState<boolean>(false)
  const [isFromLogoutButton, setIsFromLogoutButton] = useState<boolean>(false)
  const [loginError, setLoginError] = useState<string>()

  useEffect(() => {
    // Clean user context when 'unsetCookieAndUserId' is called, when the cookies and tokens are cleaned
    if (!isAuthenticated) {
      setUser(undefined)
    }
  }, [isAuthenticated])

  const logout = useCallback(async () => {
    await unsetCookieAndUserId()
  }, [unsetCookieAndUserId])

  const getCurrentUserConnexion: () => void = useCallback(
    () => {
      if (cognitoUserIdRef.current && !userLoading) {
        setUserLoading(true)
        getCurrentUser()
          .then((newUser) => {
            setUser(newUser)
          })
          .catch((reason) => {
            console.error("reason: ", reason)
            // This provider sits above the error snackbar, so the sign-in page shows the refusal itself.
            // US 2554 — a collaborator blocked by the administration is told so the same way
            if (["ORGANIZATION_IS_BLOCKED", "USER_IS_BLOCKED"].includes(getErrorCode(reason) ?? "")) {
              setLoginError(getErrorMessage(reason))
            }
            return logout()
          })
          .finally(() => setUserLoading(false))
      } else if (!userLoading) {
        setUser(undefined)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- userLoading stays out: a new callback each time a load ends would make the effect below load the user again
    [logout, getCurrentUser, unsetCookieAndUserId, cognitoUserIdRef]
  )

  useEffect(() => {
    getCurrentUserConnexion()
  }, [getCurrentUserConnexion])

  useEffect(() => {
    if (user === undefined) {
      localStorage.removeItem("user")
    } else {
      localStorage.setItem("user", JSON.stringify(user))
    }
  }, [user])

  const updateUserContext: (userUpdateDto: MyUserUpdateDto) => Promise<void> = useCallback(
    async (userUpdateDto) => {
      console.info("update user")
      try {
        setUserLoading(true)
        const updatedUser = await updateMyUser(userUpdateDto)
        setUser((prevUser) => {
          if (prevUser) {
            return {
              ...prevUser,
              givenName: updatedUser.givenName,
              email: updatedUser.email,
              familyName: updatedUser.familyName,
              phone: updatedUser.phone,
            }
          } else {
            logout()
            return undefined
          }
        })
        openSuccessSnackbar("Modification réussie !")
      } finally {
        setUserLoading(false)
      }
    },
    [logout, openSuccessSnackbar, updateMyUser]
  )

  const userStore: UserStore = useMemo(
    () => ({
      user,
      userLoading,
      updateUserContext,
      logout,
      isFromLogoutButton,
      setIsFromLogoutButton,
      getCurrentUserConnexion,
      loginError,
      clearLoginError: () => setLoginError(undefined),
    }),
    [user, userLoading, updateUserContext, logout, isFromLogoutButton, getCurrentUserConnexion, loginError]
  )
  return <UserContext.Provider value={userStore}>{children}</UserContext.Provider>
}

export interface UserStore {
  user: User | undefined
  userLoading: boolean
  isFromLogoutButton: boolean

  setIsFromLogoutButton: Dispatch<SetStateAction<boolean>>
  updateUserContext(userUpdateDto: MyUserUpdateDto): void
  logout(): Promise<void>
  getCurrentUserConnexion(): void
  /** Why the last sign-in was refused after Cognito accepted it (blocked organization). */
  loginError: string | undefined
  clearLoginError(): void
}
